import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Textarea } from "../ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { ScrollArea } from "../ui/scroll-area";
import { Plus, StickyNote, BookUser, ChevronLeft, MapPin, ArrowRight, Keyboard } from "lucide-react";

export function AddPostcodeForm({
  newPostcode,
  setNewPostcode,
  newName,
  setNewName,
  newNotes,
  setNewNotes,
  onSubmit,
  adding,
  // New props for contact selection
  contacts,
  postcodes,
  onAddFromContact
}) {
  const [activeTab, setActiveTab] = useState("contacts");
  
  // Alphabet navigation state
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [selectedName, setSelectedName] = useState(null);
  const [selectedPostcode, setSelectedPostcode] = useState(null);
  const [contactNoteInput, setContactNoteInput] = useState("");
  const [addingFromContact, setAddingFromContact] = useState(false);

  // Get all unique first letters from contacts
  const alphabet = useMemo(() => {
    const letters = new Set(
      (contacts || []).map(c => c.name.charAt(0).toUpperCase())
    );
    return Array.from(letters).sort();
  }, [contacts]);

  // Get contacts for selected letter
  const filteredContacts = useMemo(() => {
    if (!selectedLetter || !contacts) return [];
    return contacts
      .filter(c => c.name.charAt(0).toUpperCase() === selectedLetter)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [contacts, selectedLetter]);

  // Get unique names for selected letter
  const uniqueNames = useMemo(() => {
    const names = [...new Set(filteredContacts.map(c => c.name))];
    return names.sort();
  }, [filteredContacts]);

  // Get postcodes for selected name
  const postcodesForName = useMemo(() => {
    if (!selectedName || !contacts) return [];
    const pcs = [];
    contacts
      .filter(c => c.name === selectedName)
      .forEach(c => {
        const arr = c.postcodes?.length ? c.postcodes : (c.postcode ? [c.postcode] : []);
        arr.forEach(pc => {
          if (pc && !pcs.some(p => p.toUpperCase() === pc.toUpperCase())) {
            pcs.push(pc);
          }
        });
      });
    return pcs;
  }, [contacts, selectedName]);

  // Check if a postcode is already in active route
  const isInRoute = (postcode, name) => {
    return (postcodes || []).some(
      p => p.postcode.toUpperCase() === postcode.toUpperCase() && 
           p.name.toUpperCase() === name.toUpperCase() &&
           !p.completed
    );
  };

  // Handle adding from contact
  const handleAddFromContact = async () => {
    if (!selectedPostcode || !selectedName || !onAddFromContact) return;
    
    setAddingFromContact(true);
    try {
      await onAddFromContact({
        postcode: selectedPostcode,
        name: selectedName,
        notes: contactNoteInput.trim()
      });
      // Reset flow after successful add
      setSelectedPostcode(null);
      setSelectedName(null);
      setSelectedLetter(null);
      setContactNoteInput("");
    } catch (err) {
      // Error handled by parent
    } finally {
      setAddingFromContact(false);
    }
  };

  // Back button handler for contact flow
  const handleBack = () => {
    if (selectedPostcode) {
      setSelectedPostcode(null);
      setContactNoteInput("");
    } else if (selectedName) {
      setSelectedName(null);
    } else if (selectedLetter) {
      setSelectedLetter(null);
    }
  };

  // Full alphabet for display
  const fullAlphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  // Render contact selection content
  const renderContactSelection = () => {
    // Step 4: Note input form
    if (selectedPostcode) {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={handleBack} className="shrink-0 h-8 w-8">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div>
              <h3 className="font-bold text-slate-900 text-sm">{selectedName}</h3>
              <p className="text-xs text-slate-500 font-mono">{selectedPostcode}</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm text-slate-700">Add a note (optional)</Label>
            <Textarea
              value={contactNoteInput}
              onChange={(e) => setContactNoteInput(e.target.value)}
              placeholder="e.g., Ring doorbell..."
              className="min-h-[80px] text-sm"
              autoFocus
            />
          </div>

          <Button
            onClick={handleAddFromContact}
            disabled={addingFromContact}
            className="w-full h-12 rounded-full font-bold"
          >
            {addingFromContact ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Adding...
              </div>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Add to Route
              </>
            )}
          </Button>
        </div>
      );
    }

    // Step 3: Postcodes for selected name
    if (selectedName) {
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={handleBack} className="shrink-0 h-8 w-8">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div>
              <h3 className="font-bold text-slate-900">{selectedName}</h3>
              <p className="text-xs text-slate-500">Select postcode</p>
            </div>
          </div>

          <div className="space-y-2">
            {postcodesForName.map((postcode, index) => {
              const alreadyInRoute = isInRoute(postcode, selectedName);
              return (
                <button
                  key={`${postcode}-${index}`}
                  onClick={() => !alreadyInRoute && setSelectedPostcode(postcode)}
                  disabled={alreadyInRoute}
                  className={`w-full p-3 rounded-xl border text-left transition-all flex items-center justify-between ${
                    alreadyInRoute
                      ? "bg-blue-50 border-blue-200 cursor-not-allowed"
                      : "bg-white border-slate-200 hover:border-blue-300 hover:shadow-sm"
                  }`}
                >
                  <span className={`font-mono ${alreadyInRoute ? "text-blue-700" : "text-slate-900"}`}>
                    {postcode}
                  </span>
                  {alreadyInRoute ? (
                    <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                      In Route
                    </span>
                  ) : (
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    // Step 2: Contacts for selected letter
    if (selectedLetter) {
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={handleBack} className="shrink-0 h-8 w-8">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div>
              <h3 className="font-bold text-slate-900 text-xl">{selectedLetter}</h3>
              <p className="text-xs text-slate-500">{uniqueNames.length} contact{uniqueNames.length !== 1 ? 's' : ''}</p>
            </div>
          </div>

          <ScrollArea className="h-[200px]">
            <div className="space-y-2 pr-2">
              {uniqueNames.length === 0 ? (
                <p className="text-center py-4 text-slate-500 text-sm">No contacts</p>
              ) : (
                uniqueNames.map((name, index) => (
                  <button
                    key={name}
                    onClick={() => setSelectedName(name)}
                    className="w-full p-3 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm text-left transition-all flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                        {name.charAt(0)}
                      </div>
                      <span className="font-medium text-slate-900">{name}</span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      );
    }

    // Step 1: Alphabet grid
    if (!contacts || contacts.length === 0) {
      return (
        <div className="text-center py-8 text-slate-500">
          <BookUser className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm">No saved contacts yet</p>
          <p className="text-xs mt-1">Use Manual tab to add your first postcode</p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <p className="text-xs text-slate-500 text-center">Tap a letter to find contacts</p>
        <div className="grid grid-cols-9 gap-1">
          {fullAlphabet.map((letter) => {
            const hasContacts = alphabet.includes(letter);
            return (
              <button
                key={letter}
                onClick={() => hasContacts && setSelectedLetter(letter)}
                disabled={!hasContacts}
                className={`aspect-square rounded-lg font-bold text-sm flex items-center justify-center transition-all ${
                  hasContacts
                    ? "bg-blue-500 text-white hover:bg-blue-600"
                    : "bg-slate-100 text-slate-300 cursor-not-allowed"
                }`}
              >
                {letter}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Card className="border-0 shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Plus className="w-5 h-5 text-blue-500" />
          Add New Postcode
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="contacts" className="gap-2 text-sm">
              <BookUser className="w-4 h-4" />
              From Contacts
            </TabsTrigger>
            <TabsTrigger value="manual" className="gap-2 text-sm">
              <Keyboard className="w-4 h-4" />
              Manual
            </TabsTrigger>
          </TabsList>

          <TabsContent value="manual" className="mt-0">
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="postcode" className="text-slate-700">
                  Postcode
                </Label>
                <Input
                  id="postcode"
                  data-testid="postcode-input"
                  type="text"
                  placeholder="e.g. SW1A 1AA"
                  value={newPostcode}
                  onChange={(e) => setNewPostcode(e.target.value)}
                  className="h-12 rounded-xl bg-slate-100 border-transparent focus:bg-white focus:border-slate-300 uppercase"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700">
                  Location Name
                </Label>
                <Input
                  id="name"
                  data-testid="name-input"
                  type="text"
                  placeholder="e.g. Buckingham Palace"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="h-12 rounded-xl bg-slate-100 border-transparent focus:bg-white focus:border-slate-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes" className="text-slate-700 flex items-center gap-2">
                  <StickyNote className="w-4 h-4 text-amber-500" />
                  Notes <span className="text-slate-400 text-xs">(optional)</span>
                </Label>
                <Textarea
                  id="notes"
                  data-testid="notes-input"
                  placeholder="e.g., Ring doorbell, leave with neighbour if out"
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  className="min-h-[80px] rounded-xl bg-slate-100 border-transparent focus:bg-white focus:border-slate-300 text-sm"
                />
              </div>
              <Button
                type="submit"
                data-testid="add-postcode-btn"
                className="w-full h-12 rounded-full font-bold transition-transform active:scale-95"
                disabled={adding}
              >
                {adding ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Adding...
                  </div>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Postcode
                  </>
                )}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="contacts" className="mt-0">
            {renderContactSelection()}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
